import type { RequestDetail, PositionsView, PositionProposalOptions } from "./types";
import type { WorkflowDetail } from "./workflow-types";
export type WorkspaceProposal = import("../../../../core/src/offer/workspace-types").WorkspaceProposal;
export interface PreparationOptions {
  occasion: PositionProposalOptions["occasion"];
  staffing_format: PositionProposalOptions["staffingFormat"];
  duration_hours: number | null;
  include_staffing: boolean;
}
export interface WorkspacePreparation {
  options: PreparationOptions;
  saved: import("../../../../core/src/contracts/reqventos-workspace-preparation-v1").ReqventosWorkspacePreparationV1 | null;
  can_edit: boolean;
  reason: string | null;
}
export function proposalOptions(value: PreparationOptions): PositionProposalOptions {
  return { occasion: value.occasion, staffingFormat: value.staffing_format,
    durationHours: value.duration_hours, includeStaffing: value.include_staffing };
}
export function preparationOptions(value: PositionProposalOptions): PreparationOptions {
  return { occasion: value.occasion, staffing_format: value.staffingFormat,
    duration_hours: value.durationHours, include_staffing: value.includeStaffing };
}
export interface WorkspaceView {
  engine: RequestDetail["request"]["engine_pin"]["engine"];
  revision: number;
  epoch: number;
  current: boolean;
  result: RequestDetail["workflow_result"];
  facts: RequestDetail["facts"];
  draft: RequestDetail["draft"];
  preparation: WorkspacePreparation;
  processing: WorkflowDetail["processing"] | null;
  tasks: WorkflowDetail["tasks"];
  questions: Array<{ id: string; text: string }>;
  intake: WorkflowDetail["intake"];
  requirements: NonNullable<WorkflowDetail["requirements"]> | null;
  activeWorkflow: WorkflowDetail | null;
  history: WorkflowDetail | null;
  actions: RequestDetail["review_guidance"];
}
function deterministicWorkspace(data: RequestDetail): WorkspaceView {
  return { engine: "deterministic", revision: data.request.current_revision, epoch: data.request.engine_pin.engine_epoch,
    current: data.decision?.request_revision === data.request.current_revision,
    result: data.workflow_result?.engine === "deterministic" ? data.workflow_result : null,
    facts: data.facts, draft: data.draft, preparation: data.preparation,
    processing: null, tasks: [], questions: data.decision?.request_revision === data.request.current_revision && data.draft?.decision_id === data.decision.id
      ? data.draft.response_plan?.required_questions ?? [] : [], intake: null, requirements: null, activeWorkflow: null, history: data.workflow ?? null,
    actions: data.review_guidance };
}
function solWorkspace(data: RequestDetail): WorkspaceView {
  const base = deterministicWorkspace(data), flow = data.workflow, identity = flow?.result_identity;
  const current = Boolean(flow?.current && identity && identity.id === flow.result_id
    && identity.engine_epoch === data.request.engine_pin.engine_epoch
    && identity.request_revision === data.request.current_revision
    && identity.profile_hash === data.request.engine_pin.profile_hash);
  const requirements = current && flow?.processing && ["ready_for_review", "needs_operator"].includes(flow.processing.phase)
    ? flow.requirements ?? null : null;
  return { ...base, engine: "single_agent", current, requirements,
    result: current && data.workflow_result?.engine === "single_agent" ? data.workflow_result : null,
    processing: flow?.processing ?? null, activeWorkflow: flow ? { ...flow, current,
      requirements, payload: current ? flow.payload : null, tasks: current ? flow.tasks : [], intake: current ? flow.intake : null } : null,
    tasks: current ? flow?.tasks ?? [] : [], questions: current ? flow?.payload?.customer_questions ?? [] : [],
    intake: current ? flow?.intake : null, history: current ? null : flow ?? null };
}
/** Pin chooses representation; retained history never chooses current actions/content. */
export function workspaceView(data: RequestDetail): WorkspaceView {
  return data.request.engine_pin.engine === "single_agent" ? solWorkspace(data) : deterministicWorkspace(data);
}
export function workspacePositions(value: PositionsView, engine: WorkspaceView["engine"]): PositionsView | null {
  return value.engine === engine ? value : null;
}
